import * as React from 'react';

import { SchoolAdminInterface, } from './interfaces'
import { DIAGNOSTIC_INSIGHTS_IMG_SRC, generateRecommendations, STARTER, INTERMEDIATE, ADVANCED, } from './shared'

import VerificationBanner from '../verification/verification_banner'
import { requestGet, } from '../../../../../modules/request'
import { LARGE_ICON_BASE_SRC, } from '../../../../Shared/index'

const studentAccountsImg = <img alt="" src={`${LARGE_ICON_BASE_SRC}/student-accounts.svg`} />
const packsWholeImg = <img alt="" src={`${LARGE_ICON_BASE_SRC}/packs-whole.svg`} />
const activityPacksImg = <img alt="" src={`${LARGE_ICON_BASE_SRC}/activity-packs.svg`} />
const shannonHeadshotImg = <img alt="" src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/shannon-headshot.png`} />
const productBezelImg = <img alt="" className="product-bezel" src={`${DIAGNOSTIC_INSIGHTS_IMG_SRC}/product-bezel@2x.png`} />

interface LockedPageProps {
  needsEmailVerification: boolean;
  adminVerificationStatus: string;
  teacherVerificationStatus: string;
  role: string;
  schoolAdmins: SchoolAdminInterface[];
  alternativeSchoolNames: string[];
  school?: { name: string, };
}

const LockedPage = ({ school, needsEmailVerification, adminVerificationStatus, teacherVerificationStatus, role, schoolAdmins, alternativeSchoolNames, }: LockedPageProps) => {
  const [classCount, setClassCount] = React.useState<number>(null)
  const [recommendationsCount, setRecommendationsCount] = React.useState<number>(null)

  React.useEffect(() => {
    getCompleteHistory()
  }, [])

  function getCompleteHistory() {
    requestGet('/diagnostic_insights/complete_history', (body) => {
      let classCount = 0
      let recommendationsCount = 0
      Object.keys(body).forEach((classroomId) => {
        const classroom = body[classroomId]
        const ellLearningSequences = classroom.ell.filter(student => Object.keys(student.student_learning_sequences).length)
        const nonELLLearningSequences = classroom.non_ell.filter(student => Object.keys(student.student_learning_sequences).length)

        const ellRecommendations = generateRecommendations(true, ellLearningSequences, classroomId)
        const nonELLRecommendations = generateRecommendations(false, nonELLLearningSequences, classroomId)

        const ellRecommendationsCount = [STARTER, INTERMEDIATE, ADVANCED].reduce((sum, diagnosticLevel) => {
          return sum + ellRecommendations[diagnosticLevel].student_ids.length
        }, 0)

        const nonELLRecommendationsCount = [STARTER, INTERMEDIATE, ADVANCED].reduce((sum, diagnosticLevel) => {
          return sum + nonELLRecommendations[diagnosticLevel].student_ids.length
        }, 0)

        if (ellRecommendationsCount || nonELLRecommendationsCount) {
          classCount += 1
          recommendationsCount += ellRecommendationsCount + nonELLRecommendationsCount
        }
      })

      setClassCount(classCount)
      setRecommendationsCount(recommendationsCount)
    })
  }



  return (
    <div className="diagnostic-insights-locked-page container">
      <VerificationBanner
        adminVerificationStatus={adminVerificationStatus}
        alternativeSchoolNames={alternativeSchoolNames}
        needsEmailVerification={needsEmailVerification}
        role={role}
        school={school}
        schoolAdmins={schoolAdmins}
        teacherVerificationStatus={teacherVerificationStatus}
      />
      <header>
        <span className="new-tag">NEW</span>
        <h1>Diagnostic Insights</h1>
        <p className="sub-header">Make smarter diagnostic decisions with comprehensive student history and personalized recommendations. Free for verified teachers.</p>
      </header>
      <section className="call-outs">
        {!!(classCount && recommendationsCount) && (
          <div className="recommendations-available">
            <p>Recommendations available for <b>{recommendationsCount} student{recommendationsCount === 1 ? '' : 's'}</b> in <b>{classCount} of your classes</b>.</p>
          </div>
        )}
        <div>
          <b>Not verified yet?</b> You can still assign diagnostics and view reports for those you’ve assigned this school year. <a href="https://support.quill.org/en/articles/11770320-how-do-teachers-get-verified-to-access-diagnostic-insights" rel="noopener noreferrer" target="_blank">Learn more</a>
        </div>
      </section>
      {productBezelImg}
      <h2>Why Teachers Love This Report</h2>
      <section className="reasons">
        <div className="reason">
          {studentAccountsImg}
          <h3>Smart Recommendations</h3>
          <p>Get intelligent guidance on which diagnostic to assign next, based on each student's complete diagnostic history and demonstrated proficiency levels.</p>
        </div>
        <div className="reason">
          {packsWholeImg}
          <h3>Complete Performance Overview</h3>
          <p>See at a glance how your class is performing across all diagnostic skills, with clear indicators showing proficiency levels and areas needing focus.</p>
        </div>
        <div className="reason">
          {activityPacksImg}
          <h3>Historical Insights</h3>
          <p>Access a complete view of every student's diagnostic journey - including results from previous teachers and classes - all in one unified dashboard.</p>
        </div>
      </section>
      <section className="testimonial">
        {shannonHeadshotImg}
        <p className="quote">“Diagnostic Insights empowers teachers with the information they need from day one. Now, teachers can instantly see each student's complete diagnostic history and get smart recommendations for what to assign next. It eliminates the redundancy and guesswork, letting teachers focus on what matters - supporting student growth.”</p>
        <p className="attribution">Shannon Browne, Professional Learning Manager</p>
      </section>
    </div>
  )
}

export default LockedPage
